/**
 * Emitir una factura rectificativa (abono), entera o por conceptos.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 7-sep-2026: "tengo que devolver una factura y ya estamos en real, no
 * quiero meter la pata... dime qué hace primero".
 *
 * Lo que hacía era: un `¿Estás seguro?` y ya. Sin motivo, sin elegir qué se
 * devuelve, y con un número inventado contando facturas. Ahora pregunta las
 * dos cosas que hacen falta y enseña el importe antes de emitir.
 *
 * QUÉ SE ENSEÑA Y POR QUÉ
 * -----------------------
 * Los importes salen de la línea TAL COMO ESTÁ GUARDADA, no de cantidad ×
 * precio. Hay 30 facturas con el descuento metido dentro del importe y el
 * campo `discount` a 0: recalcularlas enseñaría --y abonaría-- de más. Es la
 * misma regla que aplica el servidor, a propósito: si la pantalla dijera una
 * cifra y el abono saliera por otra, nadie volvería a fiarse de la pantalla.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { mensajeDeError } from '@/lib/errores';
import { importeDeLinea } from '@/lib/importes';

const euros = (n) => `${(n ?? 0).toFixed(2)} €`;

export function DialogoRectificativa({ invoice, open, onOpenChange, onHecho }) {
  const lineas = useMemo(
    () => (invoice?.line_items?.length ? invoice.line_items : invoice?.items) || [],
    [invoice]);

  // Lo que ya se abonó en otra rectificativa no se ofrece otra vez: el
  // servidor lo rechaza, y abonarlo dos veces devolvería dos veces el dinero.
  const yaAbonadas = useMemo(() => new Set(invoice?.lineas_abonadas || []), [invoice]);

  const [elegidas, setElegidas] = useState([]);
  const [motivo, setMotivo] = useState('');
  const [emitiendo, setEmitiendo] = useState(false);

  // Al abrir se marcan todas las que quedan: devolver la factura entera es el
  // caso normal, y quitar conceptos es lo excepcional.
  useEffect(() => {
    if (open) {
      setElegidas(lineas.map((_, i) => i).filter((i) => !yaAbonadas.has(i)));
      setMotivo('');
    }
  }, [open, lineas, yaAbonadas]);

  // Con alguna ya abonada nunca es la entera: la entera abona los totales de la
  // factura, y eso incluiría lo ya devuelto.
  const todas = elegidas.length === lineas.length;
  const alternar = (i) => {
    if (yaAbonadas.has(i)) return;
    setElegidas((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort((a, b) => a - b));
  };

  const totales = useMemo(() => {
    // La rectificativa ENTERA abona los totales de la factura, no la suma de
    // sus líneas: hay facturas importadas cuyas líneas no cuadran con su
    // propio total, y lo que se devuelve tiene que ser lo que se cobró.
    if (todas) {
      return {
        base: invoice?.subtotal ?? 0,
        cuota: invoice?.iva_amount ?? invoice?.tax ?? 0,
        total: invoice?.total ?? 0,
      };
    }
    const s = elegidas.reduce((acc, i) => {
      const { base, cuota } = importeDeLinea(lineas[i]);
      return { base: acc.base + base, cuota: acc.cuota + cuota };
    }, { base: 0, cuota: 0 });
    return { ...s, total: s.base + s.cuota };
  }, [todas, elegidas, lineas, invoice]);

  const emitir = async () => {
    setEmitiendo(true);
    try {
      const { data } = await axiosInstance.post(
        `/invoices/${invoice.id}/create-return`,
        { motivo: motivo.trim(), lineas: todas ? null : elegidas });
      toast.success(`Rectificativa ${data.numero} emitida por ${euros(data.total)}`);
      onOpenChange(false);
      onHecho?.(data);
    } catch (e) {
      toast.error(mensajeDeError(e));
    } finally {
      setEmitiendo(false);
    }
  };

  const puedeEmitir = motivo.trim().length >= 3 && elegidas.length > 0 && !emitiendo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid="dialogo-rectificativa">
        <DialogHeader>
          <DialogTitle>
            Factura rectificativa de {invoice?.invoice_number}
          </DialogTitle>
          <DialogDescription>
            {invoice?.customer_name} · factura de {euros(invoice?.total)}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-amber-200 bg-amber-50 rounded p-3">
          <p className="text-amber-900 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Esto emite un documento fiscal con su propio número de serie
              (AB26-…). No se puede anular: para deshacerlo habría que emitir
              otro documento.
            </span>
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Qué se devuelve</Label>
          <div className="border rounded divide-y">
            {lineas.map((linea, i) => {
              const { base, cuota } = importeDeLinea(linea);
              const texto = linea.description || linea.product_name || 'Sin descripción';
              const abonada = yaAbonadas.has(i);
              return (
                <label key={i}
                       className={abonada
                         ? 'flex items-start gap-3 p-3 opacity-50'
                         : 'flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50'}>
                  <Checkbox
                    checked={elegidas.includes(i)}
                    disabled={abonada}
                    onCheckedChange={() => alternar(i)}
                    data-testid={`linea-${i}`}
                  />
                  <span className="min-w-0 flex-1">
                    {/* Las descripciones importadas traen la dirección del
                        cliente y saltos de línea: se corta a la primera. */}
                    <span className="block text-sm truncate">{texto.split('\n')[0]}</span>
                    <span className="block text-xs text-slate-500">
                      {linea.quantity} × {euros(linea.unit_price)}
                      {linea.tax_rate ? ` · ${linea.tax_rate}%` : ''}
                      {abonada ? ' · ya abonada' : ''}
                    </span>
                  </span>
                  <span className="text-sm font-medium whitespace-nowrap">
                    {euros(base + cuota)}
                  </span>
                </label>
              );
            })}
          </div>
          {!todas && (
            <p className="text-xs text-amber-700">
              Rectificativa parcial: se devuelven {elegidas.length} de {lineas.length} conceptos.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="motivo">Motivo de la rectificación *</Label>
          <Textarea
            id="motivo" rows={2} value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: se facturó el módulo de fichaje que el cliente no tiene contratado"
            data-testid="motivo-rectificativa"
          />
          <p className="text-xs text-slate-500">
            Sale impreso en la factura. Es obligatorio: una rectificativa tiene
            que decir qué corrige y por qué.
          </p>
        </div>

        <div className="border-t pt-3 flex justify-end">
          <div className="text-right text-sm space-y-0.5">
            <div className="text-slate-600">Base: −{euros(Math.abs(totales.base))}</div>
            <div className="text-slate-600">Impuesto: −{euros(Math.abs(totales.cuota))}</div>
            <div className="text-lg font-bold text-[#9C0F17]">
              A devolver: −{euros(Math.abs(totales.total))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={emitir} disabled={!puedeEmitir}
                  className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                  data-testid="emitir-rectificativa">
            {emitiendo ? 'Emitiendo...' : 'Emitir rectificativa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DialogoRectificativa;
